import { EventEmitter, Renderer2, RendererFactory2 } from '@angular/core';
var SelectTableService = /** @class */ (function () {
    function SelectTableService(rendererFactory, document) {
        this.document = document;
        this.onKeyUp = new EventEmitter();
        this.onKeyDown = new EventEmitter();
        this.onChange = new EventEmitter();
        this._isValid = true; // If settings were valid
        this._inputRef = null; // Reference to input
        this._tableContainer = null; // Container that holds all DOM
        this._table = null; // Reference to table
        this._tableHead = null; // Reference to table header
        this._tableBody = null; // Reference to table body
        this._parent = null; // Reference to parent
        this.filterData = []; // Data that is shown in table
        this.filterTimeout = 500; // Timeout on keyup before search
        this.filterTimeoutObject = null; // Timeout object for filter search
        this.activeRowData = null; // Selected row data
        this.dontTriggerOnKey = [
            16,
            17,
            18,
            19,
            20,
            27,
            33,
            34,
            35,
            36,
            37,
            38,
            39,
            40,
            45,
            46,
            112,
            113,
            114,
            115,
            116,
            117,
            118,
            119,
            120,
            121,
            122,
            123,
            145 // Scroll lock
        ];
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    /**
     * Function to create new instance for selecttable
     *
     * After creating you will have available functions:
     * selectTableObject will now have next functions attached:
     * showTable, // Opens dropdown
     * hideTable, // Closes dropdown
     * getValue, // Return selected row data
     * setValue, // Requires JSON with all pk keys and values, example bellow
     * getData, // Returns all data
     * setData, //Set new dataset
     * getFilteredData // Returns filtered data(if using doFilter == true)
     *
     * this.test.setValue({ id: 2 }).then((res) => {
     *     console.log(res);
     * }, (err) => {
     *     console.log(err);
     * });
     *
     *
     * USAGE EXAMPLE
     ````
        HTML -
            <mat-form-field>
                <input matInput placeholder="Text" #test>
            </mat-form-field>
  
        TS -
            @ViewChild('test') element: ElementRef;
            import { SelectTable } from '~selectTable/main';
  
            ...
            constructor(private selectTable: SelectTable) {
  
            }
            ...
  
            const selectTableObject = this.selectTable.run(this.element.nativeElement, [{
                doFilter: true, //If it should filter on keyup
                displayDelimiter: ',', //Delimiter used for spliting values
                tableColumns: [// Columns that will be in dataset
                { key: 'id', displayName: 'Id', showInTable: true, showInInput: false, isPK: true },
                { key: 'name', displayName: 'Name', showInTable: true, showInInput: true, isPK: false }
                ]
            }],
                [ // Data
                    { id: '1', name: 'Test1' },
                    { id: '2', name: 'Test2' },
                    { id: '3', name: 'Test3' }
                ]
            );
  
            // { key: 'name', displayName: 'Name', showInTable: true, showInInput: true, isPK: false }
            // key: Keyname in dataset,
            // displayName: How it should show it in table head
            // showInTable: If it should show it in table
            // showInInput: If it will show as final result in input (delimited with displayDelimiter if more than one);
            // isPK: IF it is primary key, it must have at least one PK (will be used for setting data);
    ````
    For editing any css use root class .selectTableDir (div that contains table and floats under input);
    Inside it there will be table;
    */
    SelectTableService.prototype.run = function (element, options, data) {
        this._inputRef = element;
        this._options = options[0];
        this._data = data === undefined ? [] : data;
        this.filterData = Object.assign([], this._data);
        var $this = this;
        runTableGenerator();
        return {
            showTable: showTable,
            hideTable: hideTable,
            getValue: getValue,
            setValue: setValue,
            getData: getData,
            setData: setData,
            clearValue: clearValue,
            getFilteredData: getFilteredData
        };
        function runTableGenerator() {
            $this._parent = $this._inputRef.parentNode;
            $this._tableContainer = $this.renderer.createElement('div');
            $this._tableContainer.className = 'selectTableDir';
            hideTable();
            // Prepare thead tbody and table
            $this._table = $this.renderer.createElement('table'); // Render it to table container
            $this._tableContainer.appendChild($this._table);
            $this._tableHead = $this.renderer.createElement('thead');
            $this._table.appendChild($this._tableHead);
            $this._tableBody = $this.renderer.createElement('tbody');
            $this._table.appendChild($this._tableBody);
            runStyles();
            createTableHeader();
            createTableBody();
            // Add table container to DOM;
            $this._parent.prepend($this._tableContainer);
            // Listen for input focus and blur - show hide table
            $this._inputRef.addEventListener('focus', function () {
                showTable();
            });
            $this._inputRef.addEventListener('blur', function () {
                setTimeout(function () {
                    hideTable();
                }, 100);
            });
            $this._inputRef.addEventListener('keyup', function (e) {
                if ($this._options.onKeyUp) {
                    $this._options.onKeyUp.emit(e);
                }
                if ($this.dontTriggerOnKey.indexOf(e.keyCode) === -1) {
                    if ($this._options.doFilter === true) {
                        runFilter($this._inputRef.value);
                    }
                }
            });
            $this._inputRef.addEventListener('keydown', function (e) {
                if ($this._options.onKeyDown) {
                    $this._options.onKeyDown.emit(e);
                }
            });
        }
        /**
         * Open table dropdown;
         */
        function showTable() {
            $this._tableContainer.setAttribute('hiddenSelect', false);
        }
        /**
         * Hide table dropdown;
         */
        function hideTable() {
            $this._tableContainer.setAttribute('hiddenSelect', true);
        }
        /**
         * Create table head;
         */
        function createTableHeader() {
            var tr = $this.renderer.createElement('tr');
            for (var _i = 0, _a = $this._options.tableColumns; _i < _a.length; _i++) {
                var row = _a[_i];
                if (row.showInTable === true) {
                    // If it is set so it shows in table
                    var th = $this.renderer.createElement('th'); // Create th
                    th.innerHTML = row.displayName !== undefined ? row.displayName : row.key; // Add th content
                    tr.appendChild(th);
                }
            }
            // Listen for row click and stop propagation so it wont focus element again
            tr.addEventListener('click', function (event) {
                event.stopPropagation();
            });
            $this._tableHead.appendChild(tr); // Add tr to thead
        }
        /**
         * Create table body;
         */
        function createTableBody() {
            $this._tableBody.innerHTML = ''; // Reset tbody to blank
            if ($this.filterData.length === 0) {
                var div = $this.renderer.createElement('div');
                div.classList = 'noDataDiv';
                var label = $this.renderer.createElement('label');
                label.innerHTML =
                    $this._options.noDataDisplayMessage !== undefined &&
                        $this._options.noDataDisplayMessage !== null &&
                        $this._options.noDataDisplayMessage !== ''
                        ? $this._options.noDataDisplayMessage
                        : 'No data';
                div.appendChild(label);
                $this._tableBody.appendChild(div);
            }
            else {
                var _loop_1 = function (row) {
                    // Go through DB response
                    var tr = $this.renderer.createElement('tr'); // Create row
                    tr.style.cursor = 'pointer'; // Update row styles
                    var hasData = false; // Validator
                    for (var _i = 0, _a = $this._options.tableColumns; _i < _a.length; _i++) {
                        var columns = _a[_i];
                        // Go through columns from settings
                        var key = columns.key;
                        // Check if actual value exists in DB response and if it allowed to show in table
                        if (row[key] !== undefined && columns.showInTable === true) {
                            hasData = true; // Update that it actualy had one column
                            var td = $this.renderer.createElement('td'); // Create TD and append to row
                            td.innerHTML = row[key] === '' ? '--' : row[key];
                            tr.appendChild(td);
                        }
                    }
                    if (hasData) {
                        // Check if row had any data
                        // Listen for click
                        tr.addEventListener('click', function (event) {
                            event.stopPropagation();
                            selectRow(row); // Update selection
                        });
                        $this._tableBody.appendChild(tr);
                    }
                };
                for (var _i = 0, _a = $this.filterData; _i < _a.length; _i++) {
                    var row = _a[_i];
                    _loop_1(row);
                }
            }
        }
        /**
         * Select row action callback;
         */
        function selectRow(rowData) {
            console.log('row', rowData);
            if (rowData && rowData !== undefined && rowData != null && rowData !== {}) {
                $this.activeRowData = rowData; // Set that activeRowData for this instance is selection
                var delimiter = $this._options.displayDelimiter === undefined ? ', ' : $this._options.displayDelimiter; // Collect delimiter for settings
                var inputValue = '';
                // Go through columns from settings, and if it is allowed to show in input,
                // append its value to inputValue with delimiter
                for (var _i = 0, _a = $this._options.tableColumns; _i < _a.length; _i++) {
                    var columns = _a[_i];
                    var key = columns.key;
                    var value = rowData[key];
                    if (columns.showInInput === true && value !== '') {
                        inputValue = inputValue + delimiter + value;
                    }
                }
                inputValue = inputValue.replace(delimiter, ''); // Remove first delimiter
                $this._inputRef.value = inputValue; // Change value
                /////
                // $this.onSelectChange.emit({ data: rowData, textValue: inputValue }); // Emit action onSelectChange so controller can listen to it
                hideTable();
                $this._inputRef.blur();
            }
        }
        /**
         * Run filter for data on keyup
         */
        function runFilter(value) {
            $this.filterData = [];
            for (var _i = 0, _a = $this._data; _i < _a.length; _i++) {
                var row = _a[_i];
                var keys = Object.keys(row);
                for (var _b = 0, keys_1 = keys; _b < keys_1.length; _b++) {
                    var key = keys_1[_b];
                    var rowKeyValue = row[key].toString().toLowerCase();
                    var inputValue = value.toString().toLowerCase();
                    if (rowKeyValue.indexOf(inputValue) === 0 || rowKeyValue === inputValue) {
                        $this.filterData.push(row);
                        break;
                    }
                }
            }
            createTableBody();
        }
        /**
         * Set dataset;
         */
        function setData(data_) {
            $this._data = data === undefined || data === null ? [] : data;
            $this.filterData = $this._data;
            createTableBody();
        }
        /**
         * Returns all data for select table;
         */
        function getData() {
            return $this._data;
        }
        /**
         * Return filtered data;
         */
        function getFilteredData() {
            return $this.filterData;
        }
        /**
         * Get selected row data;
         */
        function getValue() {
            return $this.activeRowData;
        }
        /**
         * Set value (row), it expects all primary keys to be sent
         */
        function setValue(primaryKeyValue) {
            return new Promise(function (resolve, error) {
                var primaryKeys = Object.keys(primaryKeyValue);
                var notFound = [];
                for (var _i = 0, _a = $this._options.tableColumns; _i < _a.length; _i++) {
                    var column = _a[_i];
                    if (column.isPK === true && primaryKeys.indexOf(column.key) === -1) {
                        notFound.push(column.key);
                    }
                }
                if (notFound.length > 0) {
                    error({
                        success: false,
                        errorMessage: 'Error assigning value to selectTable, missing keys:' + notFound.toString()
                    });
                }
                else {
                    var index = $this._data.findIndex(function (value, i) {
                        var valid = true;
                        for (var _i = 0, primaryKeys_1 = primaryKeys; _i < primaryKeys_1.length; _i++) {
                            var key = primaryKeys_1[_i];
                            if (value[key].toString() !== primaryKeyValue[key].toString()) {
                                valid = false;
                            }
                        }
                        return valid === true;
                    });
                    if (index !== -1) {
                        selectRow($this._data[index]);
                        resolve({ success: true, errorMessage: null });
                        $this.filterData = $this._data;
                        createTableBody();
                    }
                    else {
                        resolve({
                            success: false,
                            errorMessage: 'No row found to set as value'
                        });
                    }
                }
            });
        }
        /**
         * Clear value, it clears current active row
         */
        function clearValue() {
            $this._inputRef.value = '';
            $this.activeRowData = null;
        }
        // Generate styles and add them to body
        function runStyles() {
            var styles = $this.renderer.createElement('style');
            styles.setAttribute('_table_selectTable', true);
            if ($this.document.querySelectorAll('style[_table_selectTable]')[0]) {
                return;
            }
            var inputHeight = $this._inputRef.offsetHeight + 'px';
            inputHeight = '2.15em';
            styles.innerHTML =
                "\n            .selectTableDir{\n                width:100%;\n                background: white;\n                position:absolute;\n                left:0;\n                top:" +
                    inputHeight +
                    ";\n                max-height:10em;\n                min-height:4em;\n                display:block;\n                z-index:999;\n                box-shadow: 0 3px 6px 0 rgba(64, 83, 100, 0.43);\n                transition: all 0.5s ease;\n                overflow:scroll;\n            }\n            .selectTableDir[hiddenSelect=\"true\"]{\n                display:none;\n            }\n            .selectTableDir[hiddenSelect=\"false\"]{\n                display:block;\n            }\n            .selectTableDir table{\n                width: 100%;\n                max-width: 100%;\n                background-color: #fff !important;\n                font-family: 'RobotoDraft', 'Roboto', 'Helvetica Neue, Helvetica, Arial', sans-serif;\n                font-style: normal;\n                font-weight: 300;\n                font-size: 1.4rem;\n                line-height: 2rem;\n                letter-spacing: 0.01rem;\n                color: #212121;\n                background-color: #f5f5f5;\n                -webkit-font-smoothing: antialiased;\n                -moz-osx-font-smoothing: grayscale;\n                text-rendering: optimizeLegibility;\n                display:table;\n            }\n            .selectTableDir table thead{\n                display: table-header-group;\n                vertical-align: middle;\n                border-color: inherit;\n            }\n\n            .selectTableDir table thead tr, .selectTableDir table tbody tr{\n                transition: all 0.3s ease;\n                display: table-row;\n                vertical-align: inherit;\n                border-color: inherit;\n            }\n            .selectTableDir table thead tr th{\n                font-weight: 400;\n                color: #757575;\n                vertical-align: bottom;\n                border-bottom: 1px solid rgba(0, 0, 0, 0.12);\n                background-color:#fafafa;\n                text-align: left;\n                padding: 1.6rem;\n                padding-top:.5rem;\n                padding-bottom:.5rem;\n                transition: all 0.3s ease;\n                white-space: nowrap;\n                position:sticky;\n                top:0;\n            }\n            .selectTableDir table tbody{\n                display: table-row-group;\n                vertical-align: middle;\n                border-color: inherit;\n                border-collapse: separate;\n            }\n            .selectTableDir table tbody tr td{\n                font-weight: 400;\n                color: #757575;\n                vertical-align: bottom;\n                border-bottom: 1px solid rgba(0, 0, 0, 0.12);\n                text-align: left;\n                padding: 1.6rem;\n                padding-top:.5rem;\n                padding-bottom:.5rem;\n                transition: all 0.3s ease;\n                display: table-cell;\n                vertical-align: top;\n                border-top: 0;\n                transition: all 0.3s ease;\n            }\n            .selectTableDir table tbody tr:last-child td{\n                border-bottom:0 !important;\n            }\n            .selectTableDir table tbody div.noDataDiv{\n                position: absolute;\n                bottom: 0;\n                left: 50%;\n                transform: translateX(-50%);\n            }\n            ";
            $this.document.body.appendChild(styles);
        }
    };
    return SelectTableService;
}());
export { SelectTableService };
/*
EXAMPLE FOR USING;
HTML -
    <mat-form-field>
        <input matInput placeholder="Text" #test>
    </mat-form-field>

TS -
    @ViewChild('test') element: ElementRef;
    import { SelectTable } from '~selectTable/main';

    ...
    constructor(private selectTable: SelectTable) {

    }
    ...


    const selectTableObject = this.selectTable.run(this.element.nativeElement, [{
        doFilter: true, //If it should filter on keyup
        displayDelimiter: ',', //Delimiter used for spliting values
        tableColumns: [// Columns that will be in dataset
        { key: 'id', displayName: 'Id', showInTable: true, showInInput: false, isPK: true },
        { key: 'name', displayName: 'Name', showInTable: true, showInInput: true, isPK: false }
        ]
    }],
        [ // Data
            { id: '1', name: 'Test1' },
            { id: '2', name: 'Test2' },
            { id: '3', name: 'Test3' }
        ]
    );


    // selectTableObject will now have next functions attached:
    // showTable, // Opens dropdown
    // hideTable, // Closes dropdown
    // getValue, // Return selected row data
    // setValue, // Requires JSON with all pk keys and values, example bellow
    // getData, // Returns all data
    // setData, //Set new dataset
    // getFilteredData // Returns filtered data(if using doFilter == true)

    // this.test.setValue({ id: 2 }).then((res) => {
    //     console.log(res);
    // }, (err) => {
    //     console.log(err);
    // });


    // { key: 'name', displayName: 'Name', showInTable: true, showInInput: true, isPK: false }
    // key: Keyname in dataset,
    // displayName: How it should show it in table head
    // showInTable: If it should show it in table
    // showInInput: If it will show as final result in input (delimited with displayDelimiter if more than one);
    // isPK: IF it is primary key, it must have at least one PK (will be used for setting data);

*/
